/**
 * Configuration for AI summary types
 */

import { Activity, Mail, MessageSquare, Building2 } from "lucide-react";
import type { AISummaryProps } from "./types";
import type { SummaryAccentColor, SummaryTypeConfig } from "./types";

/**
 * Accent colour -> the COMPLETE Tailwind class for the header icon.
 *
 * Tailwind scans source text for whole class names, so `text-${color}-500` is
 * invisible to it: the attribute lands in the DOM, the rule never ships, and
 * the icon has silently rendered in the inherited colour since the card was
 * written. Every class here is spelled out so the scanner can see it.
 */
export const SUMMARY_ICON_COLOR_CLASS: Readonly<Record<SummaryAccentColor, string>> = {
  blue: "text-blue-500",
  green: "text-green-500",
  purple: "text-purple-500",
};

export const SUMMARY_TYPE_CONFIG: Record<string, SummaryTypeConfig> = {
  timeline: {
    icon: Activity,
    label: "Timeline Summary",
    description: "AI analysis of recent client interactions and activities",
    color: "blue",
  },
  email: {
    icon: Mail,
    label: "Email Summary",
    description: "Summary of email communications with the client",
    color: "green",
  },
  activity: {
    icon: MessageSquare,
    label: "Activity Summary",
    description: "Overview of client activities and engagement patterns",
    color: "purple",
  },
} as const;

/** Card title + description vary by CRM surface (deal detail reads as Deal summary). */
export function resolveSummaryPresentation(
  summaryType: AISummaryProps["summaryType"],
  entityType?: AISummaryProps["entityType"]
): SummaryTypeConfig {
  const base =
    SUMMARY_TYPE_CONFIG[summaryType] ??
    SUMMARY_TYPE_CONFIG.timeline;
  if (entityType === "deal" && summaryType === "timeline") {
    return {
      ...base,
      label: "Deal summary",
      description:
        "Briefing on this pipeline opportunity: focal deal facts plus communications across contacts at the linked company.",
    };
  }
  if (entityType === "company" && summaryType === "timeline") {
    return {
      ...base,
      icon: Building2,
      label: "Company briefing",
      description:
        "Account-level brief: roster, cross-contact themes, interactions, and open deals—beyond a narrow timeline slice.",
    };
  }
  return base;
}
